import clsx from "clsx";

import type { StatusPresentation } from "../statusPresentation";
import type {
  CriterionProgress,
  TierStatus,
  VolumeConverterData,
  VolumeUnit,
} from "../types";
import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import {
  BaseArrowDownIcon,
  CheckCircleFilledIcon,
  InfoIcon,
} from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { Typo } from "../../Typo";
import {
  formatCriterionValue,
  formatVolumeInUnit,
  progressRatio,
} from "../formatVolume";
import { CriteriaExplainerCard } from "../modals/CriteriaExplainerCard";
import { VolumeConverterCard } from "../modals/VolumeConverterSheet/VolumeConverterCard";

const USD_UNIT: VolumeUnit = { kind: "usd" };

const INFO_TRIGGER_CLASS =
  "flex items-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused";
const UNIT_TRIGGER_CLASS =
  "flex items-center gap-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused";

type CriterionBarProps = {
  progress: CriterionProgress;
  view?: StatusPresentation["criteriaView"];
  label: string;
  explainLabel: string;
  unitLabel?: string;
  volumeUnit?: VolumeUnit;
  status: TierStatus;
  isAtRisk?: boolean;
  lastUpdated: string | null;
  content: RebateTierTranslatedContent;
  isMobile: boolean;
  onExplain: () => void;
  isExplainerOpen: boolean;
  converter?: VolumeConverterData;
  onApplyVolumeUnit?: (unit: VolumeUnit, saveAsDefault: boolean) => void;
  onOpenConverter?: () => void;
  isConverterOpen?: boolean;
};

export const CriterionBar = ({
  progress,
  view = "progress",
  label,
  explainLabel,
  unitLabel,
  volumeUnit,
  status,
  isAtRisk = false,
  lastUpdated,
  content,
  isMobile,
  onExplain,
  isExplainerOpen,
  converter,
  onApplyVolumeUnit,
  onOpenConverter,
  isConverterOpen = false,
}: CriterionBarProps) => {
  const isPerformance = view === "performance";
  const ratio = progressRatio(progress.current, progress.target);
  const isMet = progress.target > 0 && progress.current >= progress.target;

  const display = (value: number) =>
    progress.criterion === "tv" && volumeUnit
      ? formatVolumeInUnit(value, isPerformance ? USD_UNIT : volumeUnit)
      : formatCriterionValue(progress.criterion, value);

  return (
    <div className="flex w-full flex-col gap-3">
      {!isPerformance && (
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={progress.target}
          aria-valuenow={progress.current}
          className="h-2 w-full overflow-hidden rounded-full bg-surface-tonal-default"
        >
          <div
            className={clsx(
              "h-full rounded-full",
              isAtRisk ? "bg-icon-warning-default" : "bg-surface-brand-default",
            )}
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
        <div className="flex items-center gap-1">
          <Typo
            element="span"
            smType="bodyXS"
            lgType="bodySM"
            textColor="text-text-secondary"
          >
            {label}
          </Typo>
          {isMobile ? (
            <button
              type="button"
              aria-label={explainLabel}
              aria-haspopup="dialog"
              aria-expanded={isExplainerOpen}
              onClick={onExplain}
              className={INFO_TRIGGER_CLASS}
            >
              <InfoIcon
                aria-hidden="true"
                className="h-3 w-3 shrink-0 lg:h-4 lg:w-4 [&_path]:stroke-icon-tertiary"
              />
            </button>
          ) : (
            <PopoverMenu
              place="bottom"
              align="start"
              lockPageScroll
              // !shadow-elevation beats PopoverMenu's hardcoded shadow-lg shadow-stroke-primary
              contentClassName={clsx(
                "max-h-[70vh] max-w-[calc(100vw-32px)] overflow-y-auto border border-stroke-primary p-6 !shadow-elevation",
                progress.criterion === "tv" ? "w-[328px]" : "w-[312px]",
              )}
              content={
                <CriteriaExplainerCard
                  progress={progress}
                  status={status}
                  lastUpdated={lastUpdated}
                  content={content}
                />
              }
              asChild
            >
              <button
                type="button"
                aria-label={explainLabel}
                className={INFO_TRIGGER_CLASS}
              >
                <InfoIcon
                  aria-hidden="true"
                  className="h-3 w-3 shrink-0 lg:h-4 lg:w-4 [&_path]:stroke-icon-tertiary"
                />
              </button>
            </PopoverMenu>
          )}
        </div>

        {isPerformance ? (
          <Typo
            element="span"
            smType="bodyXS"
            lgType="bodySM"
            textColor="text-text-primary"
          >
            {unitLabel
              ? `${display(progress.current)} ${content.millionUsd}`
              : display(progress.current)}
          </Typo>
        ) : (
          <div className="flex items-center gap-1">
            {isMet && (
              <CheckCircleFilledIcon
                height={16}
                width={16}
                role="img"
                aria-label={label}
                className="shrink-0"
              />
            )}
            <Typo
              element="span"
              smType="bodyXS"
              lgType="bodySM"
              textColor="text-text-primary"
            >
              {display(progress.current)}
              <span className="text-text-secondary">
                {`/${display(progress.target)}`}
              </span>
            </Typo>
            {unitLabel &&
              converter &&
              (isMobile ? (
                <button
                  type="button"
                  data-testid="rebate_tier_criterion_volume_unit_button"
                  aria-haspopup="dialog"
                  aria-expanded={isConverterOpen}
                  onClick={onOpenConverter}
                  className={UNIT_TRIGGER_CLASS}
                >
                  <Typo
                    element="span"
                    smType="bodyXS"
                    lgType="bodySM"
                    textColor="text-text-link-default"
                  >
                    {unitLabel}
                  </Typo>
                  <BaseArrowDownIcon
                    aria-hidden="true"
                    className="h-3 w-3 shrink-0 text-icon-brand-default lg:h-4 lg:w-4"
                  />
                </button>
              ) : (
                <PopoverMenu
                  place="bottom"
                  lockPageScroll
                  contentClassName="flex w-96 max-w-[calc(100vw-32px)] max-h-[70vh] flex-col p-4"
                  content={(close) => (
                    <VolumeConverterCard
                      scrollList
                      value={volumeUnit ?? USD_UNIT}
                      converter={converter}
                      content={content}
                      onApply={(unit, saveAsDefault) => {
                        onApplyVolumeUnit?.(unit, saveAsDefault);
                        close();
                      }}
                    />
                  )}
                  asChild
                >
                  <button
                    type="button"
                    data-testid="rebate_tier_criterion_volume_unit_button"
                    className={UNIT_TRIGGER_CLASS}
                  >
                    <Typo
                      element="span"
                      smType="bodyXS"
                      lgType="bodySM"
                      textColor="text-text-link-default"
                    >
                      {unitLabel}
                    </Typo>
                    <BaseArrowDownIcon
                      aria-hidden="true"
                      className="h-3 w-3 shrink-0 text-icon-brand-default lg:h-4 lg:w-4"
                    />
                  </button>
                </PopoverMenu>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
